import React, { useState } from 'react';
import Modal from '$/components/global/modal';
import BixButton from '$/components/global/bixButton';
import { TranscriptionsAdminService } from '$/services';
import * as Bixby from '$/core';

export interface IDeleteTranscriptionModalProps {
  transcription: Bixby.Models.AdminCompanyTranscription | undefined;
  isOpen: boolean;
  onClose: () => void;
  onDeleted: () => void;
  dispatchToastError?: (message: string) => Promise<void>;
  dispatchToastSuccess?: (message: string) => Promise<void>;
}

const DeleteTranscriptionModal: React.FC<IDeleteTranscriptionModalProps> = ({
  transcription,
  isOpen,
  onClose,
  onDeleted,
  dispatchToastError,
  dispatchToastSuccess,
}) => {
  const [isReplacing, setIsReplacing] = useState(false);
  const [isDeletingPermanently, setIsDeletingPermanently] = useState(false);

  if (!transcription) return null;

  const isSubmitting = isReplacing || isDeletingPermanently;

  const handleReplace = async () => {
    setIsReplacing(true);

    try {
      const result = await TranscriptionsAdminService.deleteTranscriptionAndCreateReuploadTaskAsync(
        transcription.transcriptionId
      );

      if (result.plannerTaskCreated === false) {
        if (dispatchToastError) {
          await dispatchToastError('Could not create planner task.');
        }
      } else if (result.plannerTaskCreated === true && dispatchToastSuccess) {
        await dispatchToastSuccess('Transcript deleted. A new upload task has been created.');
      } else if (dispatchToastSuccess) {
        await dispatchToastSuccess('Transcript has been deleted.');
      }

      onClose();
      onDeleted();
    } catch {
      if (dispatchToastError) {
        await dispatchToastError('Could not delete transcript.');
      }
    } finally {
      setIsReplacing(false);
    }
  };

  const handleDeletePermanently = async () => {
    setIsDeletingPermanently(true);

    try {
      await TranscriptionsAdminService.deleteTranscription(transcription.transcriptionId, true);

      if (dispatchToastSuccess) {
        await dispatchToastSuccess('Transcript has been deleted.');
      }

      onClose();
      onDeleted();
    } catch {
      if (dispatchToastError) {
        await dispatchToastError('Could not delete transcript.');
      }
    } finally {
      setIsDeletingPermanently(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Modal title="Delete Transcript" active={isOpen} onClose={handleClose}>
      <p>
        {transcription.newsExists
          ? 'Are you replacing this transcript or deleting this permanently?'
          : 'Are you sure you want to delete this transcript permanently?'}
      </p>
      <div className="button_side_wrap delete-transcription-actions">
        {transcription.newsExists && (
          <BixButton
            className="delete-transcription-action-btn"
            type="button"
            onClick={handleReplace}
            disabled={isSubmitting}
            useLoading
            isLoading={isReplacing}>
            Replace
          </BixButton>
        )}
        <BixButton
          className="delete-transcription-action-btn"
          type="button"
          onClick={handleDeletePermanently}
          disabled={isSubmitting}
          useLoading
          isLoading={isDeletingPermanently}>
          Delete Permanently
        </BixButton>
        <BixButton
          className="delete-transcription-action-btn"
          light
          type="button"
          onClick={handleClose}
          disabled={isSubmitting}>
          Cancel
        </BixButton>
      </div>
    </Modal>
  );
};

export default DeleteTranscriptionModal;
